import { useTranslation } from 'next-i18next';
import styled from 'styled-components';
import { commifyNumber } from '../../../../helpers/common';
import { PaymentInterface } from '../../../../new-lib/types/entity';
import { useSelector } from 'react-redux';
import { getPlanDuration } from '../../../../helpers/payment-plan';
import Image from 'next/image';
import { useRouter } from 'next/router';

interface ChildProps {
  plan: PaymentInterface;
  isOffer?: boolean;
}

const SelectivePlan = ({ plan, isOffer }: ChildProps) => {
  const { t } = useTranslation(['entity', 'common']);
  const { locale } = useRouter();
  const isResalePlan = !plan?.equal_installments?.value;
  const years =
    isResalePlan && plan?.years_remaining > 0
      ? plan.years_remaining
      : plan?.years && plan?.years > 0
        ? plan.years
        : null;
  const months = isResalePlan ? undefined : plan?.months;

  return (
    <Plan locale={locale}>
      <h3
        className={`plan-details ${plan.cash_discount.percent ? 'cash-discount' : ''}`}
      >
        {plan.cash_discount.percent ? (
          <div className="discount-container">
            <span className="discount-percent">
              {plan.cash_discount.percent}%
            </span>
            <span>{t('cash_discount')}</span>
          </div>
        ) : (
          <div className="installments-years-container">
            {isResalePlan ? (
              <div className="installments-container">
                <span className="starting">{t('remaining_amount')}</span>
                <div>
                  <span className={`installment-value ${isOffer && 'offer'}`}>
                    {commifyNumber(plan?.price - plan?.down_payment.value)}{' '}
                  </span>
                  <span
                    className={`installment-periodicity ${isOffer && 'offer'}`}
                  >
                    {' '}
                    {plan?.currency}
                  </span>
                </div>
              </div>
            ) : (
              <div className="installments-container">
                <span className="starting">
                  {plan.installment_type?.name === 'Back Loaded'
                    ? t('starting_from', { ns: 'common' })
                    : t('installment')}
                </span>
                <div className={`${isOffer && 'offer'}`}>
                  <span className={`installment-value ${isOffer && 'offer'}`}>
                    {commifyNumber(plan?.equal_installments.value)}{' '}
                  </span>
                  <span
                    className={`installment-periodicity ${isOffer && 'offer'}`}
                  >
                    {' '}
                    {plan?.currency}/{t('month', { ns: 'common' })}{' '}
                  </span>
                </div>
              </div>
            )}
            <div className={`years-container`}>
              <span className={`installment-years ${isOffer && 'offer'}`}>
                {years && (
                  <>
                    {getPlanDuration({ years, months })}{' '}
                    {t('years', { ns: 'common' })}
                  </>
                )}
              </span>
            </div>
          </div>
        )}
        {!plan.cash_discount.percent && (
          <>
            <div className="separator" />
            <div className="downpayment-container">
              <span>{t('downpayment')}</span>
              <div>
                <span className="downpayment-value">
                  {commifyNumber(plan?.down_payment.value)}{' '}
                </span>
                <span className="currency">{plan?.currency}</span>
              </div>
            </div>
          </>
        )}
        {plan.installment_type?.name === 'Back Loaded' && (
          <div className="escalating-section">
            <div className="separator" />

            <div className="plan-type">
              {t('escalating_payments', { ns: 'common' })}
            </div>
          </div>
        )}
        {isOffer && (
          <div className="offer-tag">
            <Image
              height={47}
              width={53}
              alt="Offer Tag"
              src={`/assets/icons/entity/offer-tag-new.svg`}
            />
          </div>
        )}
      </h3>
    </Plan>
  );
};

const Plan = styled.div<any>`
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  padding: 8px 1px;

  .plan-details {
    position: relative;
    display: flex;
    align-items: flex-end;
    border-radius: 8px;
    background: #f7f9fd;
    gap: 6px;
    padding: 12px;
    width: 470px;

    .offer-tag {
      position: absolute;
      top: 0px;
      inset-inline-end: 0px;
      ${props => props.locale === 'ar' && 'transform: scaleX(-1)'}
    }

    .separator {
      width: 1px;
      height: 40px;
      background: #d3dbe6;
    }

    .escalating-section {
      display: flex;
      gap: 12px;
      max-width: 25%;
      min-width: 25%;
    }

    .plan-type {
      color: #3e4042;
      font-family: materialMedium;
      font-size: 11px;
      align-self: center;
      width: 20%;
      display: flex;
      align-items: center;
      gap: 8px;
      .escalating-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 4px;
        background: #edf1f6;
        padding: 4px;
      }
    }

    .installments-years-container {
      display: flex;
      min-width: 44%;
      max-width: 44%;
      gap: 6px;
      align-items: flex-end;
    }

    .discount-container {
      display: flex;
      min-width: 69%;
      max-width: 69%;
      color: #ff9108;
      align-items: center;
      gap: 4px;
      align-self: center;
      .discount-percent {
        font-family: materialBold;
        font-size: 24px;
      }
      span:last-child {
        color: #ff9108;
        font-family: materialMedium;
        font-size: 12px;
        width: 50px;
      }
    }

    .installments-container {
      display: flex;
      flex-direction: column;
      gap: 4px;

      .starting {
        color: #3e4042;
        font-family: materialRegular;
        font-size: 10px;
      }

      .offer {
        color: #ff9108 !important;
      }

      .installment-value {
        color: #3e4042;
        font-family: materialSemiBold;
        font-size: 16px;
      }

      .installment-periodicity {
        color: #3e4042;
        font-family: materialMedium;
        font-size: 10px;
      }
    }

    .years-container {
      .installment-years {
        color: #3e4042;
        font-family: materialSemiBold;
        font-size: 10px;

        &.offer {
          color: #ff9108;
        }
      }
    }

    .downpayment-container {
      display: flex;
      flex-direction: column;
      gap: 4px;
      width: 22%;

      span {
        color: #3e4042;
        font-family: materialRegular;
        font-size: 10px;
      }
      div {
        .downpayment-value {
          color: #3e4042;
          font-family: materialSemiBold;
          font-size: 14px;
        }

        .currency {
          color: #3e4042;
          font-family: materialRegular;
          font-size: 10px;
        }
      }
    }
  }
`;

export default SelectivePlan;
